import { Component, HostBinding, ViewChild, ViewEncapsulation, OnInit, Input, ElementRef, ChangeDetectorRef } from '@angular/core';
import { NgForm } from '@angular/forms';
import { PopoverDirective } from 'ngx-bootstrap/popover';
import * as _ from 'underscore';
import { isArray, isNull, isNullOrUndefined, isNumber } from '../../../../models/utils';
import { Endpoint, HelpIndex, SettingsType } from '../../../../models/enums';
import { DocStorageExclusions, IndexSettings, Languages, IndexSettingsViewModel } from '../../../../models/index-settings';
import { SettingsNavigationContentComponent } from '../../../../models/settings-navigation-content-component';
import { Template } from '../../../../models/template';
import { CommonService } from '../../../../services/common/common.service';
import { RestService } from '../../../../services/rest/rest.service';
import { GenericTreeNode } from '../../../generic-tree/generic-tree-node';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { BaseModalComponent } from '../../../../models/base-modal-component';
import { DrNamedQuery } from '../../../../models/dr-named-query';
import { KeyValueListComponent } from '../../../key-value-list/key-value-list.component';
import { ObjectActionListType, Permissions, ActionType } from '../../../../models/permission';

@Component({
    standalone: false,
    selector: 'res-index-settings',
    templateUrl: './index-settings.component.html',
    styleUrls: ['./index-settings.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class IndexSettingsComponent extends BaseModalComponent implements SettingsNavigationContentComponent, OnInit {
    readonly DocStorageExclusions = DocStorageExclusions;
    readonly DefaultEmbeddedImageSize = 200;

    @HostBinding('class') classes = 'maximized-component';
    @ViewChild('errorPop') errorPop: PopoverDirective;
    @ViewChild('contentForm', { static: true }) contentForm: NgForm;
    @ViewChild('queriesKeyValue', { static: true }) queriesKeyValue: KeyValueListComponent;
    @ViewChild('customWarningErrorKeyKalue', { static: true }) customWarningErrorKeyKalue: KeyValueListComponent;
    @ViewChild('excludedFileExtensionsForExtractionKeyValue', { static: true }) excludedFileExtensionsForExtractionKeyValue: KeyValueListComponent;
    @ViewChild('excludedFileExtensionsForExtractionKeyValueViewConfig', { static: true }) excludedFileExtensionsForExtractionKeyValueViewConfig: KeyValueListComponent;
    @ViewChild('emailHeaderMappings', { static: true }) emailHeaderMappings: KeyValueListComponent;
    @ViewChild('rangeInfoArchivePopover') rangeInfoArchivePopover: PopoverDirective;
    @ViewChild('rangeInfoDocumentPopover') rangeInfoDocumentPopover: PopoverDirective;
    @ViewChild('msgArchiveProcessingTimeout') msgArchiveProcessingTimeout: ElementRef;
    @ViewChild('msgDocumentProcessingTimeout') msgDocumentProcessingTimeout: ElementRef;
    @ViewChild('extractEmbeddedImageWidthPopover') extractEmbeddedImageWidthPopover: PopoverDirective;
    @ViewChild('extractEmbeddedImageHeightPopover') extractEmbeddedImageHeightPopover: PopoverDirective;
    @Input() embeddedImageWidth: string;
    @Input() embeddedImageHeight: string;
    @Input() isDataSetConfigModal = false;

    isBusy = false;
    isBusyMessage: string;
    validChanges = true;
    helpIndex: HelpIndex;
    @Input() handle: string;
    languages: any;
    selectedLanguage: Languages;
    originalSelectedLanguage: Languages;
    docStorageExclusions: any;
    originalDocStorageExclustions = {};
    settings: IndexSettings;
    isProjectSettings: boolean;
    viewModel: IndexSettingsViewModel;
    ocrCandidatesNamedQueriesAutomationId = 'index-settings-ocr-queries-';
    customWarningsAndErrorAutomationId = 'index-settings-custom-warnings-and-errors-';
    excludedFileExtensionsForExtractionAutomationId = 'index-settings-excluded-file-extension-for-extraction-';
    customEmailHeaderFieldMappingAutomationId = 'index-settings-custom-email-header-field-mapping-';
    hasPermissions: boolean;
    isSelectedLanguageUnknown: boolean;

    defaultDocumentMsgTimeout: number;
    defaultArchiveMsgTimeout: number;
    rangeInfoText: string;
    documentTimeoutChangedToDefaultRange = false;
    archivalTimeoutChangedToDefaultRange = false;
    extractEmbeddedImagesLabel = 'Extract Embedded Images';
    noBlankInfoText = 'This field cannot be blank...';
    isSystemUser = false;

    settingsType: SettingsType;

    get isDirty(): boolean {
        if (this.contentForm) {
            return this.contentForm.dirty ||
                this.queriesKeyValue.keyValueForm.dirty || this.customWarningErrorKeyKalue.keyValueForm.form.dirty || this.excludedFileExtensionsForExtractionKeyValue.keyValueForm.form.dirty ||
                this.emailHeaderMappings.keyValueForm.form.dirty;
        }
        return false;
    }
    set isDirty(value: boolean) {
        if (this.contentForm && this.contentForm.control) {
            if (value) {
                this.contentForm.control.markAsDirty();
            } else {
                this.contentForm.control.markAsPristine();
            }
        }
    }

    constructor(private commonService: CommonService, private restService: RestService, private modalService: BsModalService,
        private modalRef: BsModalRef, private changeDetector: ChangeDetectorRef, private elemRef: ElementRef) {
        super(modalService, modalRef);
        this.docStorageExclusions = {};
        this.viewModel = new IndexSettingsViewModel(new IndexSettings); // to get page to load. Will overwrite when load.
    }

    ngOnInit() {
        this.languages = Languages;
        this.selectedLanguage = Languages.GENERAL;
        if (this.isDataSetConfigModal && !this.handle && this.commonService.currentProject) {
            this.handle = this.commonService.currentProject.handle;
        }
        this.loadSettingsData(this.handle);
    }

    loadSettingsData(handle) {
        if (handle) {
            this.isBusyMessage = 'Loading Index Settings...';
            this.isBusy = true;
            this.isDirty = false;
            if (!this.isDataSetConfigModal) {
                this.restService.post(Endpoint.projectManager_getIndexSettings, { handle: handle, systemScope: this.isSystemUser }).then(response => {
                    if (response && response.settings) {
                        this.populateIndexSettingsDataFromResponse(response.settings);
                    }
                });
            }
        }
    }

    populateIndexSettingsDataFromResponse(response: Partial<IndexSettings>): void {
        if (response) {
            if (this.isDataSetConfigModal) {
                response.externalParsingLibrary = this.commonService.externalParsingLibrary;
            }

            this.settings = new IndexSettings(response);

            if (isArray(this.settings.copyToDocStorageExclusions) || isArray(this.settings.exclusions)) {
                const docStorageExclusions = this.settings.copyToDocStorageExclusions ? this.settings.copyToDocStorageExclusions : this.settings.exclusions;
                docStorageExclusions.forEach(element => {
                    this.docStorageExclusions[element] = this.originalDocStorageExclustions[element] = true;
                });

                for (const exclusion in DocStorageExclusions) {
                    if (!this.docStorageExclusions[exclusion]) {
                        this.docStorageExclusions[exclusion] = this.originalDocStorageExclustions[exclusion] = false;
                    }
                }
            }

            this.isSelectedLanguageUnknown = this.settings.ocrLanguage === 'UNKNOWN' ? true : false;
            if (!this.isSelectedLanguageUnknown) {
                this.selectedLanguage = this.settings.ocrLanguage ? Languages[this.settings.ocrLanguage] : Languages.GENERAL;
            }
            this.settings.ocrCandidatesNamedQueries = _.sortBy(this.settings.ocrCandidatesNamedQueries, (query: any) => query.name && query.name.toLowerCase());
            const emptyNamedQuery = new DrNamedQuery({ name: '', query: '', description: null, annotationText: null });
            if (!this.isDataSetConfigModal) {
                this.settings.ocrCandidatesNamedQueries.push(new DrNamedQuery(emptyNamedQuery));
            }
            this.settings.customWarningsAndErrorNamedQueries = _.sortBy(this.settings.customWarningsAndErrorNamedQueries, (query: any) => query.name && query.name.toLowerCase());
            this.settings.customWarningsAndErrorNamedQueries.push(new DrNamedQuery(emptyNamedQuery));

            let extractionExclusions = [];
            if (this.settings.extractionExclusions && this.settings.extractionExclusions.length > 0) {
                this.settings.extractionExclusions.forEach(element => {
                    if (element) {
                        extractionExclusions.push({ name: element });
                    }
                });
            }

            extractionExclusions = _.sortBy(extractionExclusions, (query: any) => query.name && query.name.toLowerCase());
            if (this.hasPermissions) {
                extractionExclusions.push({ name: '' });
            }

            this.settings.extractionExclusions = extractionExclusions;

            if (this.settings.externalParsingLibraryDisplay === 'V1' || (!this.settings.extractEmbeddedImages && this.settings.externalParsingLibraryDisplay === 'V2')) {
                this.settings.embeddedImageHeight = 0;
                this.settings.embeddedImageWidth = 0;
            }

            if (this.settings.emailHeaderMappings) {
                for (let key in this.settings.emailHeaderMappings) {
                    this.settings.emailHeaderMappingsAsArray.push({ name: key, query: this.settings.emailHeaderMappings[key], annotationText: '', description: '' });
                }
            }
            this.settings.emailHeaderMappingsAsArray = _.sortBy(this.settings.emailHeaderMappingsAsArray, (query: any) => query.name && query.name.toLowerCase());
            if (this.hasPermissions) {
                this.settings.emailHeaderMappingsAsArray.push(new DrNamedQuery(emptyNamedQuery));
            }

            this.viewModel = new IndexSettingsViewModel(this.settings);
            this.viewModel.model.ocrLanguage = this.viewModel.original.ocrLanguage = this.selectedLanguage.toUpperCase();
            this.defaultDocumentMsgTimeout = this.viewModel.model.documentProcessingTimeout;
            this.defaultArchiveMsgTimeout = this.viewModel.model.archivalProcessingTimeout;

            this.extractEmbeddedImagesLabel = this.viewModel.model.externalParsingLibraryDisplay === 'V2' ? 'Extract Embedded Images wider than' : 'Extract Embedded Images';
            this.checkEmbeddedImages();
        }
        this.isBusy = false;
        this.isDirty = false;
        // The virtual-scroll viewport's height (--kvl-height) is only known once rows are loaded
        // above. CDK measured the viewport as ~0 at init and won't re-measure on data change, so it
        // under-renders and leaves blank space. Re-measure after the height is applied to the DOM.
        setTimeout(() => this.customWarningErrorKeyKalue?.cdkVirtualScrollViewport?.checkViewportSize());
    }

    selectedTreeItemChanged(node: GenericTreeNode, projectHandle?: string, settingsType?: SettingsType): void {
        this.settingsType = settingsType;
        this.commonService.getEnumNames(DocStorageExclusions).forEach(element => {
            this.docStorageExclusions[element] = false;
        });
        this.languages = Languages;
        this.selectedLanguage = Languages.GENERAL;
        this.handle = node.data instanceof Template ? node.data.handle :
            projectHandle ? projectHandle : this.commonService.currentOrganizationHandle;
        this.isProjectSettings = node.data instanceof Template ? false : true;
        this.helpIndex = projectHandle ? HelpIndex.case_index_settings_dlg : (settingsType === SettingsType.system ? HelpIndex.sys_template_index_settings_dlg : HelpIndex.org_index_settings_template_dlg);
        this.isSystemUser = (settingsType === SettingsType.system && (this.commonService.currentUser?.originalCustomerName === 'super_system_customer' || this.commonService.currentUser.importedFromSystemUser));
        const permissionType = projectHandle ? ObjectActionListType.PRJ_INDEX_SETTINGS : this.isSystemUser ? ObjectActionListType.SYSTEM_INDEX_SETTINGS : ObjectActionListType.ORG_INDEX_SETTINGS;
        this.hasPermissions = Permissions.checkUserPermission(permissionType, ActionType.CREATE);
        this.loadSettingsData(this.handle);
    }

    openHelp = () => this.commonService.openHelp(this.helpIndex);

    onLanguageChange() {
        this.viewModel.model.ocrLanguage = this.selectedLanguage.toUpperCase();
        this.isDirty = this.viewModel.isDirty();
    }

    onChange(docStorageExclusionType?: DocStorageExclusions) {
        this.isDirty = this.viewModel.isDirty();
        if (!this.isDirty && docStorageExclusionType) {
            if (Object.prototype.hasOwnProperty.call(this.docStorageExclusions, docStorageExclusionType)) {
                if (this.docStorageExclusions[docStorageExclusionType] !== this.originalDocStorageExclustions[docStorageExclusionType]) {
                    this.isDirty = true;
                }
            }
        }
    }

    onIncludeEmailHeadersChanged() {
        if (!this.viewModel.model.includeEmailHeaders) {
            this.viewModel.model.extractCustomEmailHeaders = false;
        }
    }

    setEmbeddedImagesPx(): void {
        if (this.viewModel.model.externalParsingLibraryDisplay === 'V2') {
            if (this.viewModel.model.extractEmbeddedImages) {
                this.viewModel.model.embeddedImageWidth = this.viewModel.model.embeddedImageWidth ?? this.DefaultEmbeddedImageSize;
                this.viewModel.model.embeddedImageHeight = this.viewModel.model.embeddedImageHeight ?? this.DefaultEmbeddedImageSize;
            } else {
                this.viewModel.model.embeddedImageWidth = null;
                this.viewModel.model.embeddedImageHeight = null;
            }
        }
    }

    saveChanges(fromAutoSave = false): Promise<boolean> {
        return new Promise<boolean>((resolve, reject) => {
            this.isBusy = true;
            this.isBusyMessage = 'Validating Queries ...';

            // Validate queries from both key-values components OCR Queries & Custom Warnings and Errors
            const queryValidations = [
                this.queriesKeyValue.validateQueries(
                    this.queriesKeyValue,
                    this.viewModel.model.ocrCandidatesNamedQueries
                ),
                this.customWarningErrorKeyKalue.validateQueries(
                    this.customWarningErrorKeyKalue,
                    this.viewModel.model.customWarningsAndErrorNamedQueries
                )
            ];

            Promise.all(queryValidations).then(([isOcrQueriesValid, isCustomWarnErrorQueriesValid]) => {
                if (!isOcrQueriesValid || !isCustomWarnErrorQueriesValid) {
                    // If one of query validations fails, resolve with false and return without saving the index settings
                    this.isBusy = false;
                    resolve(false);
                    return;
                }

                if (this.handle) {
                    // If Message Archive Processing Timeout is not within the range of 10-1200 and 'OK' is clicked onBlur
                    // the focus should be changed to the timeout input field
                    if (this.archivalTimeoutChangedToDefaultRange) {
                        const fieldToFocus = (this.msgArchiveProcessingTimeout.nativeElement as HTMLElement);
                        if (fieldToFocus) {
                            fieldToFocus.focus();
                            resolve(false);
                        }
                    }
                    // If Message Document Processing Timeout is not within the range of 3-180 and 'OK' is clicked onBlur
                    // the focus should be changed to the timeout input field
                    if (this.documentTimeoutChangedToDefaultRange) {
                        const fieldToFocus = (this.msgDocumentProcessingTimeout.nativeElement as HTMLElement);
                        if (fieldToFocus) {
                            fieldToFocus.focus();
                            resolve(false);
                        }
                    }
                    if (this.viewModel.model.ocrPageTimeout === 0 || isNullOrUndefined(this.viewModel.model.ocrPageTimeout) || !this.viewModel.model.ocrPageTimeout) {
                        this.errorPop.show();
                        resolve(false);
                    } else {
                        const ocrQueries = DrNamedQuery.removeEmptyQueries(this.viewModel.model.ocrCandidatesNamedQueries);
                        const customWarningsAndErrorQueries = DrNamedQuery.removeEmptyQueries(this.viewModel.model.customWarningsAndErrorNamedQueries);
                        const extractionExclusions = DrNamedQuery.removeEmptyNames(this.viewModel.model.extractionExclusions).map(r => r.name);

                        const allExclusions = [];
                        for (const key in this.docStorageExclusions) {
                            if (Object.prototype.hasOwnProperty.call(this.docStorageExclusions, key)) {
                                if (this.docStorageExclusions[key] === true) {
                                    allExclusions.push(key);
                                }
                            }
                        }

                        if (this.viewModel.model.extractEmbeddedImages) {
                            //prevent sending null/string value in api payload
                            this.viewModel.model.embeddedImageHeight = Number(this.viewModel.model.embeddedImageHeight) || this.DefaultEmbeddedImageSize;
                            this.viewModel.model.embeddedImageWidth = Number(this.viewModel.model.embeddedImageWidth) || this.DefaultEmbeddedImageSize;
                        } else {
                            this.viewModel.model.embeddedImageHeight = this.viewModel.model.embeddedImageWidth = 0;
                        }

                        const indexSettings = new IndexSettings(this.viewModel.model);
                        indexSettings.copyToDocStorageExclusions = allExclusions;
                        // update the view's named query variables to the model before saving.
                        indexSettings.ocrCandidatesNamedQueries = ocrQueries;
                        indexSettings.customWarningsAndErrorNamedQueries = customWarningsAndErrorQueries;
                        indexSettings.extractionExclusions = extractionExclusions;
                        indexSettings.ocrLanguage = this.selectedLanguage.toUpperCase();
                        indexSettings.emailHeaderMappings = {};
                        indexSettings.emailHeaderMappingsAsArray.forEach(v => {
                            if (v.name !== '') {
                                indexSettings.emailHeaderMappings[v.name] = v.query;
                            }
                        });

                        delete indexSettings.externalParsingLibraryDisplay;
                        delete indexSettings.emailHeaderMappingsAsArray;

                        this.isBusyMessage = 'Saving Index Settings...';
                        this.isBusy = true;
                        this.restService.post(Endpoint.projectManager_setIndexSettings, { handle: this.handle, settings: indexSettings, systemScope: this.isSystemUser }).then(response => {
                            this.isBusy = false;
                            this.isDirty = false;
                            this.resetState();
                            if (!fromAutoSave) {
                                this.loadSettingsData(this.handle);
                            }
                            resolve(true);
                        }, () => {
                            this.isBusy = false;
                            resolve(false);
                        });
                    }
                } else {
                    resolve(false);
                }
            }).catch(error => {
                this.isBusy = false;
                reject(error);
            })
        });
    }

    resetState(): void {
        if (!isNullOrUndefined(this.queriesKeyValue)) {
            this.queriesKeyValue.resetState();
        }

        if (!isNullOrUndefined(this.customWarningErrorKeyKalue)) {
            this.customWarningErrorKeyKalue.resetState();
        }
        if (!isNullOrUndefined(this.excludedFileExtensionsForExtractionKeyValue)) {
            this.excludedFileExtensionsForExtractionKeyValue.resetState();
        }
        if (!isNullOrUndefined(this.emailHeaderMappings)) {
            this.emailHeaderMappings.resetState();
        }

        this.errorPop.hide();
        this.rangeInfoArchivePopover.hide();
        this.rangeInfoDocumentPopover.hide();
        this.hidePopovers();
    }

    discardChanges(): void {
        this.isDirty = false;
        this.loadSettingsData(this.handle);
        this.resetState();
    }

    isSaveDisabled(): boolean {
        let isEnabled = false;
        const hasErrors = this.queriesKeyValue.hasErrors() || this.customWarningErrorKeyKalue.hasErrors();
        if (!this.isBusy && !hasErrors) {
            if (this.queriesKeyValue.keyValueForm.form.dirty && this.queriesKeyValue.valid) {
                isEnabled = true;
            } else if (!this.queriesKeyValue.keyValueForm.form.dirty && this.customWarningErrorKeyKalue.keyValueForm.form.dirty && this.customWarningErrorKeyKalue.valid) {
                isEnabled = true;
            } else if (!this.queriesKeyValue.keyValueForm.form.dirty && this.excludedFileExtensionsForExtractionKeyValue.keyValueForm.form.dirty && this.excludedFileExtensionsForExtractionKeyValue.valid) {
                isEnabled = true;
            } else if (!this.queriesKeyValue.keyValueForm.form.dirty && !this.customWarningErrorKeyKalue.keyValueForm.form.dirty && !this.excludedFileExtensionsForExtractionKeyValue.keyValueForm.form.dirty && this.emailHeaderMappings.keyValueForm.form.dirty && this.emailHeaderMappings.valid) {
                isEnabled = true;
            } else if (!this.queriesKeyValue.keyValueForm.form.dirty && !this.customWarningErrorKeyKalue.keyValueForm.form.dirty && !this.excludedFileExtensionsForExtractionKeyValue.keyValueForm.form.dirty && !this.emailHeaderMappings.keyValueForm.form.dirty && this.isDirty) {
                isEnabled = true;
            }
        }
        return !isEnabled;
    }

    isDiscardDisabled(): boolean {
        let isEnabled = false;
        if (!this.isBusy) {
            if (this.queriesKeyValue.keyValueForm.form.dirty || this.customWarningErrorKeyKalue.keyValueForm.form.dirty || this.excludedFileExtensionsForExtractionKeyValue.keyValueForm.form.dirty ||
                this.emailHeaderMappings.keyValueForm.form.dirty || this.isDirty) {
                isEnabled = true;
            }
        }
        return !isEnabled;
    }

    updateAutoOCRPageTimeout(value): void {
        this.errorPop.hide();
        if ((_.isNull(this.viewModel.model.ocrPageTimeout) && value?.data === '0') || (this.viewModel.model.ocrPageTimeout === 0 && value?.data === '0')) {
            this.viewModel.model.ocrPageTimeout = null;
            this.errorPop.show();
        }

        const autoOCRPageTimeoutRequired = this.viewModel.model.ocrPageTimeout ? false : true;

        if (autoOCRPageTimeoutRequired) {
            this.errorPop.show();
        }
        this.isDirty = this.viewModel.isDirty();
    }

    validateEmbeddedSizeInputOnPaste(type): void {
        setTimeout(() => {
            if (this.viewModel.model[type].match(/^[0-9]+$/) === null) {
                this.viewModel.model[type] = this.viewModel.model[type].replace(/\D/g, '');
                if (this.viewModel.model[type].startsWith('0')) {
                    this.viewModel.model[type] = this.DefaultEmbeddedImageSize;
                }
            }
            this.removeLeadingZeroes(type);
            this.changeDetector.detectChanges();
        });
    }

    restrictNumeric = (event): boolean => {
        if (event.target.value.length == 0 && event.which == 48) {
            return false;
        }
        if (event) {
            return (event.charCode === 8 || event.charCode === 0) ? false : event.charCode >= 48 && event.charCode <= 57;
        }
        return false;
    }

    removeLeadingZeroes(type): void {
        if (!this.viewModel.model[type].startsWith('0')) {
            return;
        }
        this.viewModel.model[type] = this.viewModel.model[type].slice(1);
        this.removeLeadingZeroes(type);
    }

    preventFirstZero = (event): void => {
        if (event.srcElement.value.charAt(0) == '0') {
            event.srcElement.value = event.srcElement.value.slice(1);
        }
    }

    parseNumeric = (min?: number, max?: number, type?: string): void => {
        this.rangeInfoDocumentPopover.hide();
        this.rangeInfoArchivePopover.hide();
        this.archivalTimeoutChangedToDefaultRange = false;
        this.documentTimeoutChangedToDefaultRange = false;
        let field = type === 'Document' ? this.viewModel.model.documentProcessingTimeout : this.viewModel.model.archivalProcessingTimeout;
        if (field || (field === '')) {
            if (field === '') {
                field = type === 'Document' ? this.defaultDocumentMsgTimeout : this.defaultArchiveMsgTimeout;
                this.rangeInfoText = 'This field must not be blank. The current project setting value has been entered.';
                if (type === 'Document') {
                    this.rangeInfoDocumentPopover.show();
                    this.documentTimeoutChangedToDefaultRange = true;
                } else {
                    this.rangeInfoArchivePopover.show();
                    this.archivalTimeoutChangedToDefaultRange = true;
                }
            }

            let val = parseInt(field, 10);
            if (isNaN(val)) {
                if (type === 'Document') {
                    val = this.defaultDocumentMsgTimeout;
                    this.documentTimeoutChangedToDefaultRange = true;
                } else {
                    val = this.defaultArchiveMsgTimeout;
                    this.archivalTimeoutChangedToDefaultRange = true;
                }
            }
            if (isNumber(min) && min > val) {
                val = min;
                this.rangeInfoText = `The value must be between ${min} and ${max}. It has been changed to ${min}.`;
                if (type === 'Document') {
                    this.rangeInfoDocumentPopover.show();
                    this.documentTimeoutChangedToDefaultRange = true;
                } else {
                    this.rangeInfoArchivePopover.show();
                    this.archivalTimeoutChangedToDefaultRange = true;
                }
            }
            if (isNumber(max) && val > max) {
                val = max;
                this.rangeInfoText = `The value must be between ${min} and ${max}. It has been changed to ${max}.`;
                if (type === 'Document') {
                    this.rangeInfoDocumentPopover.show();
                    this.documentTimeoutChangedToDefaultRange = true;
                } else {
                    this.rangeInfoArchivePopover.show();
                    this.archivalTimeoutChangedToDefaultRange = true;
                }
            }

            if (type === 'Document') {
                this.viewModel.model.documentProcessingTimeout = val;
            } else {
                this.viewModel.model.archivalProcessingTimeout = val;
            }
        }
    }

    onEmbeddedImageChange(): void {
        this.viewModel.model.extractEmbeddedImages = !this.viewModel.model.extractEmbeddedImages;
    }

    checkEmbeddedImages(): void {
        if (this.viewModel.model.extractEmbeddedImages) {
            this.viewModel.model.embeddedImageWidth = this.viewModel.model.embeddedImageWidth ?? this.DefaultEmbeddedImageSize;
            this.viewModel.model.embeddedImageHeight = this.viewModel.model.embeddedImageHeight ?? this.DefaultEmbeddedImageSize;
        } else {
            this.viewModel.model.embeddedImageWidth = null;
            this.viewModel.model.embeddedImageHeight = null;
        }
    }

    inputEmbeddedImagesWidthChanged(): void {
        this.extractEmbeddedImageWidthPopover?.hide();
        if (!this.viewModel.model.embeddedImageWidth) {
            this.extractEmbeddedImageWidthPopover.show();
            this.viewModel.model.embeddedImageWidth = this.DefaultEmbeddedImageSize;
        }
        this.isDirty = this.viewModel.isDirty();
    }

    inputEmbeddedImagesHeightChanged(): void {
        this.extractEmbeddedImageHeightPopover?.hide();
        if (!this.viewModel.model.embeddedImageHeight) {
            this.extractEmbeddedImageHeightPopover.show();
            this.viewModel.model.embeddedImageHeight = this.DefaultEmbeddedImageSize;
        }
        this.isDirty = this.viewModel.isDirty();
    }

    hidePopovers(): void {
        this.extractEmbeddedImageWidthPopover?.hide();
        this.extractEmbeddedImageHeightPopover?.hide();
    }

    changeHoverStyle(mouseOver): void {
        const embeddedImagesParent = document.getElementById('embeddedImagesContainer');
        if (embeddedImagesParent) {
            if (mouseOver) {
                embeddedImagesParent.classList.add('is-input-hover');
            } else {
                embeddedImagesParent.classList.remove('is-input-hover');
            }
        }
    }

    isVerticalScrollbarVisible(): boolean {
        const mainContainer = this.elemRef.nativeElement?.querySelector('.view-config')
        const extensionContainer = this.elemRef.nativeElement?.querySelector('#excludedFileExtensionsForExtractionKeyValueViewConfigId');
        const itemListContainer = extensionContainer?.querySelector('.cdk-virtual-scroll-content-wrapper');
        if (mainContainer && extensionContainer && itemListContainer) {
            return mainContainer?.scrollHeight > itemListContainer?.clientHeight;
        }
        return false;
    }

    /**
     * Explicit pixel height for a key-value-list's virtual-scroll viewport. The viewport otherwise
     * has no definite height (height:100% with no fixed-height ancestor collapses to auto), so CDK
     * measures it as ~0, under-renders the rows, and the spacer shows as blank. The row array
     * already includes the trailing empty input row, so length * row-height is the exact content
     * height. 30 mirrors the key-value-list itemSize / .key-value-row height.
     */
    keyValueListHeightPx(rows: Array<any>): string {
        return ((rows?.length || 0) * 30) + 'px';
    }

    queriesChanged(rowIndex?: number): void {
        setTimeout(() => {
            const index = rowIndex ? rowIndex : this.emailHeaderMappings.currentFocusedIndex;
            if (index > -1) {
                const formattedName = this.formatName(('drcustom_' + this.viewModel.model?.emailHeaderMappingsAsArray[index]?.name?.toLowerCase()));
                this.viewModel.model.emailHeaderMappingsAsArray[index].query = this.viewModel.model?.emailHeaderMappingsAsArray[index]?.name !== '' ? formattedName : '';
            }
        });
    }

    formatName(name: string) {
        if (name) {
            return name.replace(/[-!<>%~\"#%&*+./:;=?@\u005d\u005b^\\\\{}|\u201C\u201D\\$]/gi, '_').replace(/ /g, '_').replace(/_+/g, "_");
        }
        return '';
    }
}
